import {useAppSelector} from "../../../redux/hooks";
import {isUndefined} from "../../../util/is-type";
import {isArrayEmpty, toArray} from "../../../util/array-util";
import {deviceStore} from "../../../redux/device/deviceStore";

export type WhenDeviceGroupType = 'mobile' | 'tablet' | 'desktop' | 'web';
export type WhenDeviceGroupParamType = WhenDeviceGroupType | WhenDeviceGroupType[];
export type WhenDeviceOsType = 'ios' | 'windows' | 'mac' | 'android';
export type WhenDeviceOsParamType = WhenDeviceOsType | WhenDeviceOsType[];

const useWhenDeviceMatchTypeLogic = (allowedType?: WhenDeviceGroupParamType, blockedType?: WhenDeviceGroupParamType): boolean => {
  const isDeviceTypeMobile = useAppSelector(deviceStore.getDeviceIsMobile);
  const isDeviceTypeTablet = useAppSelector(deviceStore.getDeviceIsTablet);
  const isDeviceTypeDesktop = useAppSelector(deviceStore.getDeviceIsDesktop);

  if (isUndefined(allowedType) && isUndefined(blockedType)) {
    // we have no device types to enforce
    return true;
  }

  const deviceMatchMap: Record<WhenDeviceGroupType, boolean> = {
    mobile: isDeviceTypeMobile,
    tablet: isDeviceTypeTablet,
    desktop: isDeviceTypeDesktop,
    web: (!isDeviceTypeMobile && !isDeviceTypeTablet && !isDeviceTypeDesktop),
  };

  const allowedList = toArray(allowedType);
  const deviceMatchedToAllowedList = allowedList
    .filter((type) => deviceMatchMap[type]);

  const blockedList = toArray(blockedType);
  const deviceMatchedToBlockedList = blockedList
    .filter((type) => deviceMatchMap[type]);

  const matchResults = (
    // device must not match to blocked types
    isArrayEmpty(deviceMatchedToBlockedList)
    && (
      // must not have received allowed device type list
      isArrayEmpty(allowedList)
      // or if received allowed types, then must have matched one of list
      || !isArrayEmpty(deviceMatchedToAllowedList)
    )
  );

  return matchResults;
}

const useWhenDeviceMatchOsLogic = (allowedOs?: WhenDeviceOsParamType, blockedOs?: WhenDeviceOsParamType): boolean => {
  const isDeviceOsIOS = useAppSelector(deviceStore.getDeviceOsIsIOS);
  const isDeviceOsWindows = useAppSelector(deviceStore.getDeviceOsIsWindows);
  const isDeviceOsMac = useAppSelector(deviceStore.getDeviceOsIsMac);
  const isDeviceOsAndroid = useAppSelector(deviceStore.getDeviceOsIsAndroid);

  if (isUndefined(allowedOs) && isUndefined(blockedOs)) {
    // we have no device os to enforce, allowed
    return true;
  }

  const deviceMatchMap: Record<WhenDeviceOsType, boolean> = {
    ios: isDeviceOsIOS,
    windows: isDeviceOsWindows,
    mac: isDeviceOsMac,
    android: isDeviceOsAndroid,
  };

  const allowedList = toArray(allowedOs);
  const deviceMatchedToAllowedList = allowedList
    .filter((os) => deviceMatchMap[os]);

  const blockedList = toArray(blockedOs);
  const deviceMatchedToBlockedList = blockedList
    .filter((os) => deviceMatchMap[os]);

  return (
    // device must not match to blocked os list
    isArrayEmpty(deviceMatchedToBlockedList)
    && (
      // must not have received allowed device os list
      isArrayEmpty(allowedList)
      // or if received allowed os list, then must have matched one from list
      || !isArrayEmpty(deviceMatchedToAllowedList)
    )
  );
}

export interface IWhenDeviceLogic {
  isConditionPassed: boolean;
}

export interface IWhenDeviceLogicProps {
  allowedType?: WhenDeviceGroupType | WhenDeviceGroupType[];
  blockedType?: WhenDeviceGroupType | WhenDeviceGroupType[];
  allowedOs?: WhenDeviceOsType | WhenDeviceOsType[];
  blockedOs?: WhenDeviceOsType | WhenDeviceOsType[];
}

export const useWhenDeviceLogic = ({allowedType, blockedType, allowedOs, blockedOs}: IWhenDeviceLogicProps): IWhenDeviceLogic => {
  const isDeviceTypeMatchPassed = useWhenDeviceMatchTypeLogic(allowedType, blockedType);
  const isDeviceOsMatchPassed = useWhenDeviceMatchOsLogic(allowedOs, blockedOs);

  const isConditionPassed = (
    isDeviceTypeMatchPassed
    && isDeviceOsMatchPassed
  );

  return {
    isConditionPassed,
  };
}
